/*!
 * 多選週期選擇器（dropdown / panel 兩種模式）
 *
 * ⚠️ v2.35.0 重畫過一次。改版前它是全站唯一還在用「藍紫漸層 + 光暈」那套
 *    配色的元件（trigger 藍紫漸層、送出鈕洋紅、面板 `#8fc0ff` 標題、
 *    多層 boxShadow 與 backdropFilter），跟收斂後的 design token 對打，
 *    在每一頁都是最吵的東西（Brad 2026-09-10：「有點太醜，跟現在全域的 CSS
 *    有點衝突」）。
 *
 * 現在的規則跟其他元件一致：
 *   - 表面用 `COLORS.cardBg` / `COLORS.inputBg`，1px 邊框，圓角 8~10，不打光
 *   - 強調色只用 `COLORS.navActive`（亮青）—— 日曆圖示、開啟狀態的邊框、
 *     已選中的項目，全部同一個色，不再出現第二、第三種主色
 *   - 送出用全站共用的 `COLORS.accentGradient`，不再自己調一個洋紅
 *
 * 🔴 trigger 與底下的按鈕都**不用 reactstrap `<Button>`** —— 原因見
 *    `TinyWeekWindowNav` 檔頭（主題會對 `.btn` 加位移與 margin）。
 *
 * 🔑 trigger 高度用 `WEEK_CTRL_HEIGHT`，跟「上/下 N 週」是同一個常數 ——
 *    它們永遠並排，高度必須綁在一起，不能各寫各的。
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { COLORS } from "constant/adminTheme";
import { WEEK_CTRL_HEIGHT } from "./TinyWeekWindowNav";

const ghostBtn = {
  height: 28,
  boxSizing: "border-box",
  padding: "0 11px",
  margin: 0,
  borderRadius: 6,
  border: `1px solid ${COLORS.border}`,
  background: "rgba(255,255,255,0.05)",
  color: COLORS.textSecondary,
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  lineHeight: 1,
};

function TinyMultiWeekSelector({
  options = [],
  selectedValues = [],
  onChange,
  onApply,
  onCancel,
  mode = "panel",
  title = "週期選擇",
  placeholder = "搜尋週期...",
  triggerText = "選擇週期",
  triggerBlock = false,
  triggerMinWidth = 220,
  dropdownMinWidth = 320,
  dropdownMaxWidth = 420,
  dropdownAlign = "right",
  maxListHeight = 260,
}) {
  const rootRef = useRef(null);
  const [keyword, setKeyword] = useState("");
  const [open, setOpen] = useState(false);

  const filteredOptions = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    if (!k) return options;
    return options.filter((w) => String(w).toLowerCase().includes(k));
  }, [options, keyword]);

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const toggleWeek = (week) => {
    if (!onChange) return;
    if (selectedSet.has(week)) {
      onChange(selectedValues.filter((w) => w !== week));
    } else {
      onChange([...selectedValues, week].sort().reverse());
    }
  };

  const selectAll = () => onChange && onChange([...options]);
  const clearAll = () => onChange && onChange([]);

  const handleCancel = () => {
    if (onCancel) onCancel();
    if (mode === "dropdown") setOpen(false);
  };

  const handleApply = () => {
    if (onApply) onApply();
    if (mode === "dropdown") setOpen(false);
  };

  useEffect(() => {
    if (mode !== "dropdown" || !open) return undefined;
    const onDocClick = (event) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target)) setOpen(false);
    };
    const onEsc = (event) => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [mode, open]);

  const panel = (
    <div
      style={{
        background: COLORS.cardBg,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 10,
        padding: 12,
        boxShadow: "0 10px 26px rgba(0,0,0,0.45)",
      }}
    >
      <div
        className="d-flex justify-content-between align-items-center mb-2"
        style={{ gap: 10, flexWrap: "wrap" }}
      >
        <div style={{ color: COLORS.textPrimary, fontSize: 13, fontWeight: 600 }}>
          <i className="fa fa-calendar mr-2" style={{ color: COLORS.navActive }} />
          {title}
          <span style={{ color: COLORS.textMuted, fontWeight: 500, marginLeft: 6, fontSize: 12 }}>
            已選 {selectedValues.length} 週
          </span>
        </div>
        <div className="d-flex" style={{ gap: 6 }}>
          <button type="button" style={ghostBtn} onClick={selectAll}>全選</button>
          <button type="button" style={ghostBtn} onClick={clearAll}>全不選</button>
        </div>
      </div>

      <div style={{ position: "relative", marginBottom: 10 }}>
        <i
          className="fa fa-search"
          style={{
            position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)",
            color: COLORS.textDimmed, fontSize: 12, pointerEvents: "none",
          }}
        />
        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder={placeholder}
          style={{
            width: "100%", height: 34, boxSizing: "border-box",
            background: COLORS.inputBg, color: COLORS.textPrimary,
            border: `1px solid ${COLORS.borderInput}`, borderRadius: 8,
            padding: "0 10px 0 31px", fontSize: 13, outline: "none",
          }}
        />
      </div>

      <div
        style={{
          maxHeight: maxListHeight,
          overflowY: "auto",
          borderRadius: 8,
          border: `1px solid ${COLORS.border}`,
          background: "rgba(0,0,0,0.18)",
          padding: 6,
        }}
      >
        {filteredOptions.length === 0 ? (
          <div style={{ color: COLORS.textMuted, fontSize: 12, padding: "8px 4px" }}>無符合的週期</div>
        ) : (
          filteredOptions.map((week) => {
            const on = selectedSet.has(week);
            return (
              <label
                key={week}
                style={{
                  cursor: "pointer", display: "flex", alignItems: "center",
                  color: on ? COLORS.textPrimary : COLORS.textSecondary,
                  marginBottom: 2, borderRadius: 6, padding: "6px 8px",
                  // 已選：亮青淡染 + 左側一條亮青。用 borderLeft 而不是整圈邊框，
                  // 才不會在選/未選之間讓每一列的寬度差 2px。
                  background: on ? "rgba(17,205,239,0.10)" : "transparent",
                  borderLeft: `2px solid ${on ? COLORS.navActive : "transparent"}`,
                  fontWeight: on ? 600 : 400,
                }}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleWeek(week)}
                  style={{ marginRight: 9, accentColor: COLORS.navActive, width: 15, height: 15 }}
                />
                <span style={{ fontSize: 13, letterSpacing: "0.2px" }}>{week}</span>
              </label>
            );
          })
        )}
      </div>

      <div className="d-flex justify-content-between align-items-center mt-2">
        <div style={{ color: COLORS.textMuted, fontSize: 11 }}>可選 {options.length} 週期</div>
        <div className="d-flex" style={{ gap: 8 }}>
          <button type="button" style={{ ...ghostBtn, height: 30, minWidth: 66 }} onClick={handleCancel}>
            取消
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={selectedValues.length === 0}
            style={{
              height: 30, minWidth: 66, boxSizing: "border-box", padding: "0 14px", margin: 0,
              borderRadius: 6, border: "none", background: COLORS.accentGradient,
              color: "#fff", fontSize: 12, fontWeight: 700, lineHeight: 1,
              opacity: selectedValues.length === 0 ? 0.45 : 1,
              cursor: selectedValues.length === 0 ? "not-allowed" : "pointer",
            }}
          >
            套用
          </button>
        </div>
      </div>
    </div>
  );

  if (mode === "panel") return panel;

  return (
    <div
      ref={rootRef}
      /* ⚠️ triggerBlock 時根元素也要 `width: 100%` —— 只給 trigger 設 100%
            是不夠的：它算的是這個 wrapper 的寬，而 wrapper 若是 flex item
            就只有內容寬。實測（v2.35.0）級別詳情那一欄因此縮到 200px
            並被推到欄位最右邊，跟上面的 Label 差了 196px。 */
      style={{
        display: triggerBlock ? "block" : "inline-block",
        width: triggerBlock ? "100%" : undefined,
        position: "relative",
        maxWidth: "100%",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          minWidth: triggerMinWidth,
          width: triggerBlock ? "100%" : undefined,
          maxWidth: "100%",
          height: WEEK_CTRL_HEIGHT,
          boxSizing: "border-box",
          margin: 0,
          padding: "0 12px",
          borderRadius: 8,
          // 開啟時只換邊框顏色，寬度不變 → trigger 不會在開合時跳動
          border: `1px solid ${open ? COLORS.navActive : COLORS.borderInput}`,
          background: COLORS.inputBg,
          color: COLORS.textPrimary,
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          transition: "border-color 0.16s ease",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
          <i className="fa fa-calendar" style={{ color: COLORS.navActive, fontSize: 13 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {triggerText}
          </span>
        </span>
        <i
          className="fa fa-chevron-down"
          style={{
            fontSize: 10, color: COLORS.textMuted, flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.16s ease",
          }}
        />
      </button>
      {open ? (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: dropdownAlign === "right" ? 0 : "auto",
            left: dropdownAlign === "left" ? 0 : "auto",
            zIndex: 1080,
            marginTop: 6,
            minWidth: dropdownMinWidth,
            maxWidth: `min(${dropdownMaxWidth}px, 92vw)`,
          }}
        >
          {panel}
        </div>
      ) : null}
    </div>
  );
}

export default TinyMultiWeekSelector;
